import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface ComboOption {
    id: string;
    /** Bold first line. */
    primary: string;
    /** Muted second line. */
    secondary: string;
}

interface Props {
    /** Accessible name for the input. */
    label: string;
    placeholder: string;
    query: string;
    onQueryChange: (query: string) => void;
    /** Already filtered by the caller - this component does not match. */
    options: ComboOption[];
    onSelect: (id: string) => void;
    /**
     * Open the list on focus even with an empty query.
     *
     * On for the item field: a store's list is short enough to browse. Off for
     * the store field, where there is no useful first page of ~1,900.
     */
    openOnFocus?: boolean;
    /** Shown when the field is empty and the list is not open. */
    emptyHint: string;
    /** Shown when a query matches nothing. */
    noMatchHint: string;
}

/**
 * A text field that is also a dropdown.
 *
 * Custom rather than a native `<datalist>`: the rows carry two lines (a name and
 * an item or store number), datalist gives no control over that, and its mobile
 * behaviour is inconsistent - Safari in particular. These users are on phones.
 *
 * Follows the ARIA combobox pattern: the input keeps focus throughout and the
 * active option is pointed at with `aria-activedescendant`, so options are
 * `role="option"` list items rather than buttons. Buttons inside a listbox would
 * be easier to wire up and wrong.
 */
export function Combobox({
    label,
    placeholder,
    query,
    onQueryChange,
    options,
    onSelect,
    openOnFocus = false,
    emptyHint,
    noMatchHint,
}: Props) {
    const [focused, setFocused] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [active, setActive] = useState(-1);
    const listId = useId();
    const blurTimer = useRef<number | undefined>(undefined);

    const hasQuery = query.trim() !== '';
    const wantsOpen = focused && !dismissed && (hasQuery || openOnFocus);
    const open = wantsOpen && options.length > 0;

    // A new query invalidates the old highlight - keeping it would select a row
    // the associate never looked at.
    useEffect(() => {
        setActive(-1);
        setDismissed(false);
    }, [query]);

    // The blur handler below delays closing so a mousedown on an option lands
    // before the list unmounts. That timer must not outlive the component or a
    // tap-away-then-back within the delay.
    useEffect(() => () => window.clearTimeout(blurTimer.current), []);

    const choose = (id: string) => {
        onSelect(id);
        setActive(-1);
        setDismissed(false);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setDismissed(true);
            setActive(-1);
            return;
        }
        if (!open) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => (i + 1) % options.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (i <= 0 ? options.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            if (active >= 0 && active < options.length) {
                e.preventDefault();
                choose(options[active].id);
            }
        }
    };

    return (
        <div className="combo">
            <label className="field">
                <input
                    className="control"
                    role="combobox"
                    aria-label={label}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listId : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        open && active >= 0 ? `${listId}-${active}` : undefined
                    }
                    value={query}
                    placeholder={placeholder}
                    onChange={(e) => onQueryChange(e.target.value)}
                    onFocus={() => {
                        // Cancel any pending close from a previous blur - tapping
                        // back into the field within the delay must not have it
                        // fire out from under the associate. Also start this
                        // visit to the field fresh: an Escape from before should
                        // not keep the list shut forever.
                        window.clearTimeout(blurTimer.current);
                        setFocused(true);
                        setDismissed(false);
                    }}
                    // Delayed so a click on an option lands before the list unmounts.
                    onBlur={() => {
                        blurTimer.current = window.setTimeout(() => setFocused(false), 120);
                    }}
                    onKeyDown={onKeyDown}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </label>

            {open && (
                <ul className="combo-list" role="listbox" id={listId}>
                    {options.map((option, i) => (
                        <li
                            key={option.id}
                            id={`${listId}-${i}`}
                            role="option"
                            aria-selected={i === active}
                            className={`combo-option${i === active ? ' is-active' : ''}`}
                            // mouseDown, not click: blur would otherwise close the
                            // list before a click ever landed.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                choose(option.id);
                            }}
                            onMouseEnter={() => setActive(i)}
                        >
                            <b>{option.primary}</b>
                            <span>{option.secondary}</span>
                        </li>
                    ))}
                </ul>
            )}

            {!open && hasQuery && options.length === 0 && (
                <p className="hint">{noMatchHint}</p>
            )}
            {!hasQuery && !open && <p className="hint">{emptyHint}</p>}
        </div>
    );
}
